import { useState } from 'react'
import {
  useAdminAlertRecipients,
  useCreateAdminAlertRecipient,
  useUpdateAdminAlertRecipient,
  useDeleteAdminAlertRecipient,
} from '../../../hooks/admin/useCatchAllAlerts'
import type { AdminContactType } from '../../../services/admin/alertsApi'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PHONE_REGEX = /^\+?[0-9][0-9\s\-().]{6,20}$/

export default function RecipientsPage() {
  const { data: recipients, isLoading } = useAdminAlertRecipients()
  const createRecipient = useCreateAdminAlertRecipient()
  const updateRecipient = useUpdateAdminAlertRecipient()
  const deleteRecipient = useDeleteAdminAlertRecipient()

  const [contactType, setContactType] = useState<AdminContactType>('EMAIL')
  const [contact, setContact] = useState('')
  const [label, setLabel] = useState('')
  const [error, setError] = useState<string | null>(null)

  const emails = (recipients ?? []).filter((r) => r.contactType === 'EMAIL')
  const phones = (recipients ?? []).filter((r) => r.contactType === 'SMS')

  const handleAdd = async () => {
    setError(null)
    const value = contact.trim()
    if (contactType === 'EMAIL' && !EMAIL_REGEX.test(value)) return setError('Enter a valid email address.')
    if (contactType === 'SMS' && !PHONE_REGEX.test(value)) return setError('Enter a valid phone number.')

    await createRecipient.mutateAsync({ contactType, contact: value, label: label.trim() || null, enabled: true })
    setContact('')
    setLabel('')
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold">Catch-All Recipients</h1>
        <p className="text-gray-600 mt-1">
          These emails and phone numbers receive a copy of every catch-all alert, regardless of who owns the device.
          Intended for admin monitoring and testing (3–5 of each recommended).
        </p>
      </div>

      {/* Add form */}
      <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold mb-4">Add recipient</h2>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">Type</label>
            <select
              value={contactType}
              onChange={(e) => setContactType(e.target.value as AdminContactType)}
              className="px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="EMAIL">Email</option>
              <option value="SMS">Phone (SMS)</option>
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium mb-1">{contactType === 'EMAIL' ? 'Email address' : 'Phone number'}</label>
            <input
              type={contactType === 'EMAIL' ? 'email' : 'tel'}
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              placeholder={contactType === 'EMAIL' ? 'name@example.com' : '+15555550123'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="block text-sm font-medium mb-1">Label (optional)</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Ops on-call"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <button
            type="button"
            onClick={handleAdd}
            disabled={createRecipient.isPending || !contact.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {createRecipient.isPending ? 'Adding...' : 'Add'}
          </button>
        </div>
        {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
      </section>

      {isLoading ? (
        <p className="text-gray-600">Loading recipients...</p>
      ) : (
        <div className="grid gap-6 md:grid-cols-2">
          <RecipientList
            title="Emails"
            rows={emails}
            onToggle={(id, enabled) => updateRecipient.mutate({ id, input: { enabled } })}
            onDelete={(id) => deleteRecipient.mutate(id)}
          />
          <RecipientList
            title="Phone numbers"
            rows={phones}
            onToggle={(id, enabled) => updateRecipient.mutate({ id, input: { enabled } })}
            onDelete={(id) => deleteRecipient.mutate(id)}
          />
        </div>
      )}
    </div>
  )
}

function RecipientList({
  title,
  rows,
  onToggle,
  onDelete,
}: {
  title: string
  rows: Array<{ id: number; contact: string; label: string | null; enabled: boolean }>
  onToggle: (id: number, enabled: boolean) => void
  onDelete: (id: number) => void
}) {
  return (
    <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold mb-4">{title}</h2>
      {rows.length === 0 ? (
        <p className="text-gray-500 text-sm">None configured.</p>
      ) : (
        <ul className="divide-y divide-gray-200">
          {rows.map((row) => (
            <li key={row.id} className="flex items-center gap-3 py-3">
              <label className="flex items-center cursor-pointer" title="Enabled">
                <input
                  type="checkbox"
                  checked={row.enabled}
                  onChange={(e) => onToggle(row.id, e.target.checked)}
                  className="rounded border-gray-300"
                />
              </label>
              <div className="flex-1 min-w-0">
                <p className={`truncate ${row.enabled ? 'text-gray-900' : 'text-gray-400 line-through'}`}>{row.contact}</p>
                {row.label && <p className="text-xs text-gray-500 truncate">{row.label}</p>}
              </div>
              <button
                type="button"
                onClick={() => onDelete(row.id)}
                className="text-sm text-red-600 hover:text-red-800"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
